import type { MuiTheme as Theme, PresentationFilePreviewProps as FilePreviewProps } from '@paintscout/ui'
import {
  makeStyles,
  Button,
  InputLabel,
  Spinner,
  PresentationDropzone as Dropzone,
  PresentationFilePreview as FilePreview,
  DEFAULT_IMAGE_ACCEPT
} from '@paintscout/ui'
import classnames from 'classnames'
import React, { useCallback } from 'react'
import { useSnackbar } from 'notistack'
import type { StyleClasses } from '@paintscout/ui/styles'
import type { QuoteFile } from 'paintscout'
import type { UploadOptions } from '@paintscout/util/s3'

import { useImageUpload } from '../UploadImage/useImageUpload'
import type { ImageUploadResult } from '../UploadImage/useImageUpload'

const useStyles = makeStyles<Theme, Partial<PresentationUploadImageProps>>(
  (theme) => ({
    root: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start'
    },
    size: {},
    label: {
      marginBottom: theme.spacing(1)
    },
    dropzone: {
      position: 'relative',
      flexGrow: 1,
      '& $button': {
        // material-ui will spit out a warning about not being able to find $button, but it still works
        // https://github.com/mui-org/material-ui/issues/15511
        minHeight: (props: Partial<PresentationUploadImageProps>) => (props.buttonHeight ? props.buttonHeight : 0)
      }
    },
    button: {
      height: '100%'
    },
    above: {
      display: 'flex',
      alignItems: 'space-between'
    },
    clearButton: {
      marginTop: theme.spacing()
    },
    clearWrapper: (props) => ({
      display: 'flex',
      justifyContent: props.avatar ? 'center' : 'start',
      width: '100%'
    }),
    acceptDrop: {},
    rejectDrop: {
      '& $button': {
        borderColor: theme.palette.grey.A100
      }
    },
    previewOverlay: {
      position: 'absolute',
      top: 0,
      bottom: 0,
      right: 0,
      left: 0,
      background: 'rgba(255,255,255,0.7)'
    },
    preview: {
      width: '100%',
      height: '100%'
    },
    img: {
      '&:not($disabled)': {
        cursor: 'pointer'
      },
      width: '100%',
      height: '100%',
      objectFit: 'contain'
    },
    image: (props) => ({
      borderRadius: props?.avatar ? '2em' : null,
      height: props.avatar ? '28px' : null,
      width: props.avatar ? '28px' : null
    }),
    disabled: {}
  }),
  { name: 'PresentationUploadImage' }
)

export interface PresentationUploadImageProps {
  classes?: StyleClasses<typeof useStyles>
  uploadOptions?: UploadOptions
  /**
   * use publicId if possible
   */
  src?: string
  label?: string
  className?: string
  disabled?: boolean
  cloudinaryPublicId?: string
  s3PublicKey?: string
  format?: string
  pages?: number
  square?: boolean
  noClearButton?: boolean
  showSwapOnHover?: boolean
  accept?: string
  /** Disables click-to-open-native-file-picker on the underlying dropzone (drag & drop still works). */
  noClick?: boolean
  /**
   * Stops drag/drop events from bubbling past this dropzone. Needed when it sits inside another
   * drop area (a Gallery tile inside the block's own FileDropArea — #23709); without it one drop
   * is handled twice and the files upload twice.
   */
  noDragEventsBubbling?: boolean
  maxWidth?: number
  maxHeight?: number
  buttonHeight?: number
  quality?: number
  presentationOptions?: QuoteFile['presentationOptions']
  avatar?: boolean
  isUploading?: boolean
  showAllPages?: boolean
  companyId?: string
  s3UploadFeatureEnabled?: boolean
  s3MediaUploadEnabled?: boolean
  onUpload: (args: {
    src: string
    cloudinaryPublicId: string
    s3PublicKey: string
    format: string
    pages: number
    width: number
    height: number
    type: string
  }) => any
  /**
   * Opt-in multi-file upload (#23709). When set, a drop of several files uploads them all and
   * reports them in one call; `onUpload` is not called. Without it the drop keeps using only the
   * first file.
   */
  onUploadMultiple?: (results: ImageUploadResult[]) => any
  onClear: () => any
  filePreviewProps?: Partial<FilePreviewProps>
}

/**
 * Presentation-editor variant of {@link UploadImage} (#21305). Shares the upload
 * orchestration (`useImageUpload`) with the core UploadImage but renders the
 * presentation FilePreview so its editor-only preview features (square, swap-on-hover,
 * multi-page) keep working, without dragging those into the core image component.
 */
function PresentationUploadImage({
  className,
  src,
  cloudinaryPublicId,
  s3PublicKey,
  format,
  pages,
  label,
  square,
  noClearButton,
  showSwapOnHover,
  disabled,
  isUploading,
  onClear,
  onUpload,
  onUploadMultiple,
  maxWidth,
  maxHeight,
  buttonHeight,
  presentationOptions,
  accept = DEFAULT_IMAGE_ACCEPT,
  noClick,
  noDragEventsBubbling,
  uploadOptions,
  showAllPages,
  filePreviewProps,
  companyId,
  s3UploadFeatureEnabled,
  s3MediaUploadEnabled,
  ...props
}: PresentationUploadImageProps) {
  const classes = useStyles({ buttonHeight, ...props })
  const { enqueueSnackbar } = useSnackbar()

  const hasPublicId = !!cloudinaryPublicId || !!s3PublicKey

  const { loading, handleDrop } = useImageUpload({
    maxWidth,
    maxHeight,
    uploadOptions,
    onUpload,
    onUploadMultiple,
    companyId,
    s3UploadFeatureEnabled,
    s3MediaUploadEnabled
  })

  // Without this, a rejected drop (wrong type, too large) produces zero visible feedback —
  // indistinguishable from nothing happening at all. See docs/pitfalls/README.md, "A
  // file-type/accept check keyed on `file.type` can silently drop the file...".
  const handleDropRejected = useCallback(() => {
    enqueueSnackbar('Unable to upload files of this format.', { variant: 'error' })
  }, [enqueueSnackbar])

  const isLoading = isUploading || loading

  return (
    <div
      {...props}
      className={classnames(
        {
          [classes.root]: true,
          [classes.disabled]: !!disabled
        },
        className
      )}
    >
      <div className={classes.above}>
        {label && (
          <InputLabel shrink={true} className={classes.label}>
            {label}
          </InputLabel>
        )}
      </div>
      <Dropzone
        className={classes.dropzone}
        onDrop={handleDrop}
        onDropRejected={handleDropRejected}
        disabled={isLoading || disabled}
        accept={accept}
        noClick={noClick}
        noDragEventsBubbling={noDragEventsBubbling}
        shouldRenderPreview={isLoading || !!hasPublicId}
        renderPreview={() => {
          return (
            <div className={classes.preview}>
              {isLoading ? (
                <div className={classes.previewOverlay}>
                  <Spinner fullWidth={true} fullHeight={true} />
                </div>
              ) : (
                <FilePreview
                  classes={{ image: classes.image, size: classes.size }}
                  square={square}
                  showSwapOnHover={showSwapOnHover && !disabled}
                  showAllPages={showAllPages}
                  file={{
                    cloudinaryPublicId,
                    s3PublicKey,
                    src,
                    type: 'image',
                    format,
                    pages,
                    visibility: 'visible',
                    presentationOptions: presentationOptions || {}
                  }}
                  {...filePreviewProps}
                />
              )}
            </div>
          )
        }}
      />
      {!isLoading && !noClearButton && (
        <div className={classes.clearWrapper}>
          <Button className={classes.clearButton} disabled={!src && !hasPublicId} onClick={onClear}>
            Clear Image
          </Button>
        </div>
      )}
    </div>
  )
}

export default PresentationUploadImage
